import React, { useCallback, useEffect, useState } from "react";
import { Box, Card, CardHeader, CardContent, Button, Typography, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Tooltip, Chip, IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import UserAuthentication from "../../../components/userAuthentication/index";
import TreatmentsModal from "../treatments/treatmentModal";
import ProcedureList from "../treatments/procedures/procedureList";
import SignTreatmentModal from "./treatmentSignModal";
import api from "../../../api/api";
import { useNavigate, useParams } from "react-router-dom";
import Loading from "../../../components/loading";
import SuccessMessage from "../../../components/successMessage";
import ErrorMessage from "../../../components/errorMessage";

export default function TreatmentsList(reloadTreatmentsFlag) {

    const { patientId } = useParams();

    const navigate = useNavigate();

    const [isLoading, setLoading] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const [treatments, setTreatments] = useState([]);
    const [selectedTreatment, setSelectedTreatment] = useState(null);
    const [treatmentForActions, setTreatmentForActions] = useState(null);
    const [isNewTreatmentModalOpen, setIsNewTreatmentModalOpen] = useState(false);
    const [anchorEl, setActionsMenuAnchorEl] = useState(null);
    const [showProcedureComponent, setShowProcedureComponent] = useState(false);
    const [isSignModalOpen, setIsSignModalOpen] = useState(false);

    const getTreatmentsFromPatient = useCallback(() => {
        if (!patientId) {
            return;
        }
        setLoading(true);
        api.get("/treatments/".concat(patientId), {
            headers: {
                Authorization: sessionStorage.getItem('token')
            }
        }).then((returnFromApi) => {
            setTreatments(returnFromApi.data);
            setLoading(false);
        })
            .catch((returnFromApi) => {
                setLoading(false);
                console.error("ops! ocorreu um erro" + returnFromApi);
                setErrorMessage(returnFromApi.response.data);
                if (returnFromApi.status === 401) {
                    navigate("/expired-session");
                }
            });
    }, [patientId, navigate]);

    useEffect(() => {
        getTreatmentsFromPatient();
    }, [patientId, getTreatmentsFromPatient]);

    const openTreatmentModal = () => setIsNewTreatmentModalOpen(true);
    const closeTreatmentModal = () => setIsNewTreatmentModalOpen(false);

    const setTreatmentClicked = (event, treatment) => {
        setActionsMenuAnchorEl(event.currentTarget);
        setTreatmentForActions(treatment);
        setSelectedTreatment(treatment);
    };

    const closeTreatmentActionsMenu = () => setActionsMenuAnchorEl(null);

    const onTreatmentSaved = (returnFromApi) => {
        if (returnFromApi.data) {
            setShowSuccessMessage(true);
            setSuccessMessage(returnFromApi.data);
        } else {
            setShowErrorMessage(true)
            setErrorMessage(returnFromApi.response.data);
        }
        getTreatmentsFromPatient();
        closeTreatmentModal();
    };

    const onProcedureSaved = () => {
        getTreatmentsFromPatient();
    };

    const openProcedures = () => {
        setShowProcedureComponent(true);
        closeTreatmentActionsMenu();
    };

    const openSignModal = () => {
        setIsSignModalOpen(true);
        closeTreatmentActionsMenu();
    };

    const confirmTreatmentSign = () => {
        setIsSignModalOpen(false);

        setLoading(true);
        api.post(`/treatments/${selectedTreatment.treatmentId}/sign`, null, {
            headers: {
                Authorization: sessionStorage.getItem('token')
            }
        }).then((returnFromApi) => {
            setShowSuccessMessage(true);
            setSuccessMessage(returnFromApi.data);
            setLoading(false);
            getTreatmentsFromPatient();
        }).catch((returnFromApi) => {
            setLoading(false);
            setShowErrorMessage(true);
            setErrorMessage(returnFromApi.response.data);
            console.error("ops! ocorreu um erro" + returnFromApi);
        });
    };

    const deleteSelectedTreatment = () => {
        closeTreatmentActionsMenu();

        setLoading(true);
        api.delete(`/treatments/${selectedTreatment.treatmentId}`, {
            headers: {
                Authorization: sessionStorage.getItem('token')
            }
        }).then((returnFromApi) => {
            setShowSuccessMessage(true);
            setSuccessMessage(returnFromApi.data);
            setLoading(false);
            setShowProcedureComponent(false);
            getTreatmentsFromPatient();
        }).catch((returnFromApi) => {
            setLoading(false);
            setShowErrorMessage(true);
            setErrorMessage(returnFromApi.response.data);
            console.error("ops! ocorreu um erro" + returnFromApi);
        });

    };

    const printTreatment = () => {
        window.open(`/treatment-to-print/${selectedTreatment.treatmentId}`, "_blank");
    };

    const treatmentClick = (treatment) => {
        setSelectedTreatment(treatment);
        setShowProcedureComponent(true);
    };

    return (
        <>
            <Loading isToHidden={isLoading === false} />
            {showSuccessMessage && <SuccessMessage message={successMessage} show={showSuccessMessage} setShow={setShowSuccessMessage} />}
            {showErrorMessage && <ErrorMessage message={errorMessage} show={showErrorMessage} setShow={setShowErrorMessage} />}

            <Box sx={{ display: 'flex', alignItems: 'start' }}>
                <Box sx={{ flex: '1.5', minWidth: 0 }}>
                    <UserAuthentication />
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardHeader
                            title={<Typography variant="h5" sx={{ fontWeight: 'bold' }}>Tratamentos</Typography>}
                            action={
                                <Button variant="outlined" disableElevation onClick={openTreatmentModal}>
                                    Adicionar Tratamento
                                </Button>
                            }
                        />
                        <CardContent>
                            {!isLoading && treatments.length === 0 ? (
                                <Typography align="center" sx={{ py: 4 }}>
                                    Nenhum tratamento cadastrado ainda.
                                </Typography>

                            ) : (
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Convênio</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Dentista</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Tratamento</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Valor Total</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                                <TableCell></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {treatments.map((treatment) => (
                                                <TableRow
                                                    key={treatment.treatmentId}
                                                    hover
                                                    onClick={() => treatmentClick(treatment)}
                                                    selected={selectedTreatment?.treatmentId === treatment.treatmentId}
                                                    sx={{ cursor: 'pointer' }}
                                                >
                                                    <TableCell>{treatment.covenant}</TableCell>
                                                    <TableCell>{treatment.dentistName}</TableCell>
                                                    <TableCell>{treatment.description}</TableCell>
                                                    <TableCell>
                                                        {(treatment.formattedTotalAmount || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={treatment.status}
                                                            color={
                                                                treatment.status === 'Assinado' ? 'success' :
                                                                    treatment.status === 'Finalizado' ? 'primary' :
                                                                        'info'
                                                            }
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <IconButton onClick={(event) => {
                                                            event.stopPropagation();
                                                            setTreatmentClicked(event, treatment)
                                                        }}>
                                                            <MoreVertIcon />
                                                        </IconButton>

                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </CardContent>
                    </Card>

                    <Menu anchorEl={anchorEl} onClose={closeTreatmentActionsMenu} open={Boolean(anchorEl)}>
                        <MenuItem onClick={openProcedures}>Ver Procedimentos</MenuItem>
                        <Tooltip placement="left"
                            title={treatmentForActions?.status === 'Assinado' ? "Este tratamento já foi assinado." : ""}>
                            <span>
                                <MenuItem onClick={openSignModal} disabled={treatmentForActions?.status === 'Assinado'}>
                                    Assinar Tratamento
                                </MenuItem>
                            </span>
                        </Tooltip>
                        <MenuItem onClick={printTreatment} >Imprimir</MenuItem>
                        <Tooltip
                            placement="left"
                            title={treatmentForActions?.status === 'Assinado' ? "Não é possível deletar um tratamento já assinado." : ""}
                        >
                            <span>
                                <MenuItem
                                    onClick={deleteSelectedTreatment}
                                    sx={{ color: 'error.main' }}
                                    disabled={treatmentForActions?.status === 'Assinado'}
                                >
                                    Deletar
                                </MenuItem>
                            </span>
                        </Tooltip>
                    </Menu>

                    <TreatmentsModal open={isNewTreatmentModalOpen} onCloseTreatmentModal={closeTreatmentModal}
                        onTreatmentSaved={onTreatmentSaved} />
                </Box>

                {showProcedureComponent && (
                    <Box sx={{ flex: '1', minWidth: 0 }}>
                        <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                            <CardContent>
                                <ProcedureList treatment={selectedTreatment} onProcedureSaved={onProcedureSaved} />
                            </CardContent>
                        </Card>
                    </Box>
                )}
                {/* {showProcedureComponent && (
                    <ProcedureModal isOpen={isProcedureModalOpen} onClose={closeNewTreatmentModal} treatment={selectedTreatment} onSaveSuccess={onProcedureSaveSuccess} />
                )} */}
            </Box>
            <SignTreatmentModal open={isSignModalOpen} onClose={() => setIsSignModalOpen(false)} onConfirm={confirmTreatmentSign} treatmentDescription={treatmentForActions?.descricao || ''} />
        </>
    );
}